import * as React from 'react';
import { Box } from '@chakra-ui/core';
import { get, set } from '../../utils/index';

const FormContext = React.createContext(undefined);

const updateReducer = (name: string, data: any, error: string, validations) => (state: any) => {
    const { errors, value } = state;
    const nextValue = { ...value };
    const nextErrors = { ...errors };

    set(nextValue, name, data);

    if (errors[name]) {
        const nextError = error || (validations[name] && validations[name](data, nextValue));
        if (nextError) {
            nextErrors[name] = nextError;
        } else {
            delete nextErrors[name];
        }
    }

    return {
        value: nextValue,
        errors: nextErrors
    };
};

interface FormProps {
    method?: string;
    action?: string;
    theme?: any;
    data?: any;
    value?: any;
    errors?: any;
    messages?: any;
    url?: any;
    onChange?: (value: any) => void;
    onSubmit: (e: React.FormEvent) => void;
    onReset?: (e: React.FormEvent) => void;
}

interface FormState {
    errors: any;
    value: any;
    messages: any;
}

class Form extends React.Component<FormProps, FormState> {

    private form: HTMLFormElement;
    validations: { [key: string]: (name: string, value: any) => any } = {}

    constructor(props: FormProps) {
        super(props);

        this.state = {
            errors: {},
            value: { ...props.data },
            messages: {
                required: 'Field is required',
                invalid: 'Invalid value provided'
            }
        };
    }

    onSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        const { onSubmit, url } = this.props;
        const { errors, value } = this.state;

        event.preventDefault();

        const nextErrors = { ...errors };
        Object.keys(this.validations).forEach((name) => {
            const validate = this.validations[name];
            const error = validate && validate(get(value, name), value);
            if (error) {
                nextErrors[name] = error;
            } else {
                delete nextErrors[name];
            }
        });

        if (Object.keys(nextErrors).length === 0 && onSubmit) {
            event.persist();
            const adjustedEvent: any = event;
            adjustedEvent.value = value;

            // if a url was provided, submit it
            if (url && url !== '') {
                this.submitForm();
            } else {
                onSubmit(adjustedEvent);
            }
        } else {
            this.setState({ errors: nextErrors });
        }
    }

    onReset = (event: React.FormEvent<HTMLFormElement>) => {
        const { onChange, onReset } = this.props;
        const value = {};
        this.setState({
            errors: {},
            value
        }, () => {
            if (onReset) {
                event.persist();
                const adjustedEvent: any = event;
                adjustedEvent.value = value;
                onReset(adjustedEvent);
            }
            if (onChange) {
                onChange(value);
            }
        });
    }

    getForm(): HTMLFormElement {
        return this.form;
    }

    addValidation = (name: string, validate: any) => {
        this.validations[name] = validate;
    }

    update = (name: string, data: any, error: any) => {
        const { onChange } = this.props;
        this.setState(updateReducer(name, data, error, this.validations), () => {
            const { value } = this.state;
            if (onChange) {
                onChange(value);
            }
        });
    }

    submit() {
        if (this.form) {
            this.form.dispatchEvent(new Event('submit'));
        }
    }

    private async submitForm(): Promise<boolean> {
        const { url } = this.props;
        return new Promise<boolean>((resolve, reject) => {
            fetch(url).then((response) => {
                resolve(true);
            }).catch((error) => {
                reject(error);
            });
        });
    }

    render() {
        const { children, theme, onSubmit, ...rest } = this.props;
        const { errors, value, messages } = this.state;

        delete rest.messages;
        delete rest.value;

        return (
            <FormContext.Provider value={{
                addValidation: this.addValidation,
                isSubmitting: false,
                errors,
                messages,
                value,
                update: this.update,
                theme
            }}
            >
                <Box as="form" ref={(ref) => { this.form = ref; }} {...rest} onReset={this.onReset} onSubmit={this.onSubmit}>
                    {children}
                </Box>
            </FormContext.Provider>
        );
    }
}

export { FormContext };

export default Form;
